import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  limit,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  QueryConstraint,
  Timestamp,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore'
import { db } from './firebaseConfig'

// Product interface
export interface FirestoreProduct {
  id?: string
  title: string
  description: string
  shortDescription?: string
  price: number
  originalPrice?: number
  category?: string
  platform?: string
  format?: string
  skillLevel?: string
  industry?: string
  creatorType?: string
  priceRange?: string
  licenseType?: string
  useCase?: string
  tags?: string[]
  demoUrl?: string
  image?: string
  previewImages?: string[]
  productFileUrl?: string
  sellerName?: string
  sellerId?: string
  status?: 'draft' | 'pending' | 'published' | 'rejected'
  createdAt?: number
  updatedAt?: number
  // Video-specific fields
  videoUrl?: string
  videoType?: string
  isVideo?: boolean
  // Video metadata fields
  videoMetadata?: {
    width?: number
    height?: number
    isVertical?: boolean
    duration?: number
  }
}

// Pagination result interface
export interface PaginatedResult<T> {
  items: T[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
  hasMore: boolean
}

// Get products with pagination
export const getProducts = async (
  limitCount: number = 12,
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null,
  filters: Record<string, any> = {},
): Promise<PaginatedResult<FirestoreProduct>> => {
  try {
    const productsRef = collection(db, 'products')
    const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')]
    // Add limit constraint
    constraints.push(limit(limitCount))
    // Add startAfter for pagination if lastDoc is provided
    if (lastDoc) {
      constraints.push(startAfter(lastDoc))
    }
    // Add filters if provided
    if (filters.category) {
      constraints.push(where('category', '==', filters.category))
    }
    if (filters.trending) {
      constraints.push(where('isTrending', '==', true))
    }
    if (filters.newArrivals) {
      constraints.push(where('isNewArrival', '==', true))
    }
    if (filters.sellerId) {
      constraints.push(where('sellerId', '==', filters.sellerId))
    }
    // Execute query
    const q = query(productsRef, ...constraints)
    const querySnapshot = await getDocs(q)
    // Process results
    const products: FirestoreProduct[] = []
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      products.push({
        id: doc.id,
        ...data,
        // Convert Firestore Timestamp to JS Date if needed
        createdAt:
          data.createdAt instanceof Timestamp
            ? data.createdAt.toDate()
            : data.createdAt,
      } as FirestoreProduct)
    })
    // Determine if there are more results
    const hasMore = !querySnapshot.empty && querySnapshot.size >= limitCount
    // Get the last document for next pagination
    const lastVisible =
      querySnapshot.docs[querySnapshot.docs.length - 1] || null
    return {
      items: products,
      lastDoc: lastVisible,
      hasMore,
    }
  } catch (error) {
    console.error('Error fetching products:', error)
    throw error
  }
}

// Get single product by ID
export const getProductById = async (
  productId: string,
): Promise<FirestoreProduct> => {
  try {
    const productRef = doc(db, 'products', productId)
    const productSnapshot = await getDoc(productRef)
    if (productSnapshot.exists()) {
      const data = productSnapshot.data()
      return {
        id: productSnapshot.id,
        ...data,
        // Convert Firestore Timestamp to JS Date if needed
        createdAt:
          data.createdAt instanceof Timestamp
            ? data.createdAt.toDate()
            : data.createdAt,
      } as FirestoreProduct
    } else {
      throw new Error('Product not found')
    }
  } catch (error) {
    console.error('Error fetching product:', error)
    throw error
  }
}

// Add a new product
export const createProduct = async (product: FirestoreProduct) => {
  try {
    const productsRef = collection(db, 'products')
    const docRef = await addDoc(productsRef, {
      ...product,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating product:', error)
    throw error
  }
}

// Update a product
export const updateProduct = async (
  productId: string,
  updates: Partial<FirestoreProduct>,
) => {
  try {
    const productRef = doc(db, 'products', productId)
    // Always include updatedAt with any product update
    await updateDoc(productRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    })
    return true
  } catch (error) {
    console.error('Error updating product:', error)
    throw error
  }
}

// Delete a product
export const deleteProduct = async (productId: string) => {
  try {
    const productRef = doc(db, 'products', productId)
    await deleteDoc(productRef)
    return true
  } catch (error) {
    console.error('Error deleting product:', error)
    throw error
  }
}

// Get products by seller
export const getProductsBySeller = async (sellerId: string) => {
  try {
    const productsRef = collection(db, 'products')
    const q = query(productsRef, where('sellerId', '==', sellerId))
    const querySnapshot = await getDocs(q)
    const products: FirestoreProduct[] = []
    querySnapshot.forEach((doc) => {
      products.push({ id: doc.id, ...doc.data() } as FirestoreProduct)
    })
    return products
  } catch (error) {
    console.error('Error fetching seller products:', error)
    throw error
  }
}

// Get orders by seller
export const getOrdersBySeller = async (sellerId: string) => {
  try {
    const ordersRef = collection(db, 'orders')
    const q = query(ordersRef, where('sellerId', '==', sellerId))
    const querySnapshot = await getDocs(q)
    const orders: any[] = []
    querySnapshot.forEach((doc) => {
      orders.push({ id: doc.id, ...doc.data() })
    })
    return orders
  } catch (error) {
    console.error('Error fetching seller orders:', error)
    throw error
  }
}

// Get payouts by seller
export const getPayoutsBySeller = async (sellerId: string) => {
  try {
    const payoutsRef = collection(db, 'payouts')
    const q = query(payoutsRef, where('sellerId', '==', sellerId))
    const querySnapshot = await getDocs(q)
    const payouts: any[] = []
    querySnapshot.forEach((doc) => {
      payouts.push({ id: doc.id, ...doc.data() })
    })
    return payouts
  } catch (error) {
    console.error('Error fetching seller payouts:', error)
    throw error
  }
}

// Backfill updatedAt field for existing products
export const backfillProductTimestamps = async () => {
  try {
    console.log('Starting product timestamp backfill...')
    const productsRef = collection(db, 'products')
    const querySnapshot = await getDocs(productsRef)
    let updateCount = 0
    let errorCount = 0
    // Process in batches to avoid Firestore limits
    const batchSize = 500 // Firestore batch limit
    const batches = []
    let currentBatch = []
    console.log(
      `Found ${querySnapshot.size} total products to check for backfill`,
    )
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data()
      const productRef = doc(db, 'products', docSnapshot.id)
      // Only update documents missing updatedAt
      if (!data.updatedAt) {
        // Use createdAt as updatedAt if available, otherwise use current server timestamp
        const updatedAt = data.createdAt || serverTimestamp()
        // Add to current batch
        currentBatch.push({
          ref: productRef,
          data: { updatedAt },
        })
        updateCount++
        // When batch reaches limit, add to batches array and reset
        if (currentBatch.length >= batchSize) {
          batches.push([...currentBatch])
          currentBatch = []
        }
      }
    })
    // Add any remaining updates to batches
    if (currentBatch.length > 0) {
      batches.push(currentBatch)
    }
    console.log(
      `Preparing to update ${updateCount} products in ${batches.length} batches`,
    )
    // Process all batches sequentially
    for (let i = 0; i < batches.length; i++) {
      const batch = writeBatch(db)
      const currentBatchItems = batches[i]
      currentBatchItems.forEach((item) => {
        batch.update(item.ref, item.data)
      })
      try {
        await batch.commit()
        console.log(
          `Batch ${i + 1}/${batches.length} completed successfully (${currentBatchItems.length} items)`,
        )
      } catch (err) {
        console.error(`Error in batch ${i + 1}:`, err)
        errorCount += currentBatchItems.length
      }
    }
    console.log(
      `Backfill complete. Updated ${updateCount - errorCount} products. Errors: ${errorCount}`,
    )
    return {
      updated: updateCount - errorCount,
      errors: errorCount,
      total: querySnapshot.size,
    }
  } catch (error) {
    console.error('Error backfilling product timestamps:', error)
    throw error
  }
}
